import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";

import { useCampaignContext } from "../context/CampaignContext";

import type {
  AudienceFacets,
  AudienceFilters,
  AudienceProfile,
} from "../types";

export interface AudienceSearchResponse {
  total: number;
  data: AudienceProfile[];
  facets: AudienceFacets;
}

interface AudienceSearchPagingParams {
  page: number;
  pageSize: number;
}

async function getAudienceWithFacetsRequest(
  workspaceId: string,
  campaignId: string,
  page: number,
  filters: AudienceFilters,
  size: number,
): Promise<AudienceSearchResponse> {
  const queryParams = new URLSearchParams();
  queryParams.set("page", page.toString());
  queryParams.set("size", size.toString());

  const response = await post(
    `workspaces/${workspaceId}/campaigns/${campaignId}/audience-search-with-facets?${queryParams.toString()}`,
    filters,
  );

  return response.audience;
}

export default function useAudienceSearch(
  filters: AudienceFilters,
  pagingParams: AudienceSearchPagingParams,
) {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { id: campaignId } = useCampaignContext();
  const { page, pageSize } = pagingParams;

  const { campaignsKeys } = getQueryKeys(workspaceId);

  const {
    data,
    isLoading: isLoadingAudience,
    isFetching: isFetchingAudience,
    isError: isAudienceError,
    refetch: refetchAudience,
  } = useQuery({
    queryKey: campaignsKeys.audienceSearch(campaignId, filters, pagingParams),
    queryFn: () =>
      getAudienceWithFacetsRequest(
        workspaceId,
        campaignId,
        page,
        filters,
        pageSize,
      ),
    placeholderData: keepPreviousData,
    gcTime: 0,
  });

  const audience = data?.data?.filter(
    (profile): profile is AudienceProfile => !!profile?.urn,
  );
  const audienceFacets = data?.facets;
  const audienceTotal = data?.total;

  return {
    audience,
    audienceTotal,
    isLoadingAudience,
    isFetchingAudience,
    isAudienceError,
    refetchAudience,
    audienceFacets,
  };
}
